<template>
  <v-container grid-list-md>
    <v-row>
      <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-card>
          <v-toolbar v-if="showHeader || isAdmin">
            <v-toolbar-title v-if="showHeader">{{ name }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <template v-if="editActive">
              <v-btn @click="showHeader = !showHeader">{{ showHeader ? 'Hide' : 'Show' }} Header</v-btn>
              <v-btn @click="editActive = false">Cancel Edits</v-btn>
              <v-btn @click="saveChanges()">Save Changes</v-btn>
            </template>
            <v-btn v-else @click="startEdit()">Edit Show</v-btn>
          </v-toolbar>
          <v-img v-if="editActive || !hideImage" :src="`/img/theatre/${showId}.jpg`" height="300" @error="handleImageError">
            <v-dialog v-if="isAdmin && showId && editActive" v-model="imageDialog" width="300">
              <template #activator="{ on }">
                <v-btn v-on="on" fab color="success" style="float:right">
                  <v-icon>fas fa-image</v-icon>
                </v-btn>
              </template>
              <v-card v-if="showId">
                <v-card-title>Upload Cover Image</v-card-title>
                <v-card-text>
                  <v-file-input v-model="imageUpload" label="Upload Image" accept="image/jpeg" outlined>
                    <template #append-outer>
                      <v-btn class="mr-2" style="transform:translateY(-6px)" @click="uploadImage">Upload</v-btn>
                    </template>
                  </v-file-input>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-img>
          <html-editor v-if="isAdmin && editActive" v-model="editDescription" />
          <v-card-text v-else-if="description" class="theatre-event-description" v-html="description" />
        </v-card>
        <v-card class="mt-2">
          <v-card-text v-if="isAdmin && editActive">
            <h2>Performances</h2>
            <v-data-table :items="performances" :headers="performanceHeaders">
              <template #item.prices="{ item }">
                <v-chip v-for="{ label, price, limit } of item.prices" :key="item._id + '-' + label" outlined small label>{{ label }}: ${{ price + (limit ? ` (Limit ${limit})` : '') }}</v-chip>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-text v-else>
            <h2>Performances</h2>
            <p>Please choose a performance below to purchase your tickets for that performance.</p>
            <v-list>
              <!-- <purchase-dialog
                v-for="perf of performances"
                :key="'perf-' + perf._id"
                :show-id="showId"
                :name="name"
                :performance="perf" /> -->
              <purchase-expander
                v-for="perf of performances"
                :key="'perf-' + perf._id"
                :show-id="showId"
                :name="name"
                :performance="perf"
                :is-admin="isAdmin"
                @update="(upd) => updateTickets(perf, upd)" />
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-dialog v-if="isAdmin && editActive" v-model="addPerformanceDialog" width="600">
              <template #activator="{ on }">
                <v-btn v-on="on">Add Performance</v-btn>
              </template>
              <v-card>
                <v-card-title>Add Performance</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <date-picker v-model="performanceDate" label="Performance Date" outlined />
                    </v-col>
                    <v-col>
                      <time-picker v-model="performanceTime" label="Performance Time" outlined />
                    </v-col>
                    <v-col>
                      <v-text-field v-model="performanceTickets" label="Ticket Count" type="number" outlined />
                    </v-col>
                  </v-row>
                  <p>Below are the price options. Student and Employee prices are special and require a login to access, all other prices will be available to everyone.</p>
                  <p class="mb-6">The Ticket Limit is how many tickets can be purchased at that price (total in the case of Student/Employee tickets, per-transaction for others). Leave it blank for no limit.</p>
                  <v-row v-for="({ label, price, limit }, index) of prices" :key="'price-' + index">
                    <v-col>
                      <v-combobox :value="label" :items="['Covenant Student', 'Covenant Faculty/Staff', 'Community Adult', 'Community Student/Elder']" label="Price Label" outlined hide-details @change="(label) => updatePrice(index, { label })" />
                    </v-col>
                    <v-col>
                      <v-text-field :value="price" label="Price Amount" type="number" outlined hide-details @change="(price) => updatePrice(index, { price })" />
                    </v-col>
                    <v-col>
                      <v-text-field :value="limit" label="Ticket Limit" type="number" hint="Leave blank for no limit" outlined hide-details clearable @change="(limit) => updatePrice(index, { limit })" />
                    </v-col>
                  </v-row>
                  <v-btn class="mt-4" @click="prices.push({ label: '', price: 0, limit: null })">Add Price Option</v-btn>
                </v-card-text>
                <v-card-actions>
                  <v-btn :disabled="isAdding || !performanceDate || !performanceTime" text @click="addPerformance()">Add Performance</v-btn>
                  <v-spacer />
                  <v-btn text @click="addPerformanceDialog = false">Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-actions>
        </v-card>
        <v-card v-if="tickets.length > 0">
          <v-card-text>
            <p v-if="isLoggedIn">Since you are logged in, this purchase will be linked to your Covenant account:<br/>Name: {{ user.preferred }} {{ user.last }}<br/>Email: {{ user.email }}</p>
            <v-row v-else>
              <v-col cols="12" class="pb-0">
                <p>Please complete this form to purchase your tickets.</p>
                <p>You are currently not logged in. If you are an employee or student and want to see these options, please log in.</p>
              </v-col>
              <v-col>
                <v-text-field v-model="first" label="First Name" outlined hide-details />
              </v-col>
              <v-col>
                <v-text-field v-model="last" label="Last Name" outlined hide-details />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="email" label="Email Address" outlined />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn :disabled="!(isLoggedIn || (first && last && email))" @click="saveTickets()">{{ paymentTotal > 0 ? 'Pay with PayPal' : 'Submit Reservation' }}</v-btn>
          </v-card-actions>
          <payment-popup ref="paymentPopup" :total="paymentTotal" :items="paymentItems" :show-activator="false" department="Theatre" :foapal="{ fund: '9999', org: '9999', acct: '99999', prog: '99' }" @success="paymentSuccessful" @cancel="cancelTickets" />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<style>
div.theatre-event-description h1, div.theatre-event-description h2, div.theatre-event-description h3 {
  margin-bottom: .3em;
}
</style>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import axios from 'axios'

export default {
  components: {
    HtmlEditor: () => import('@/components/forms/HTMLEditor'),
    DatePicker: () => import('@/components/forms/inputs/DatePicker'),
    TimePicker: () => import('@/components/forms/inputs/TimePicker'),
    PaymentPopup: () => import('@/components/core/PaymentPopup'),
    PurchaseExpander: () => import('@/components/theatre/purchaseExpander')
    // PurchaseDialog: () => import('@/components/theatre/purchaseDialog')
  },
  setup (props, { root }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const isLoggedIn = computed(() => root.$store.state.loggedIn)
    const isAdmin = computed(() => isLoggedIn.value && user.value.roles.includes('Technology Services'))
    const showId = ref('')
    const shortName = computed(() => root.$route.params.show)
    const showHeader = ref(true)
    const name = ref('')
    const hideImage = ref(false)
    const description = ref('')
    const editDescription = ref('')
    const editActive = ref(false)
    const performances = ref([])
    const performanceHeaders = ref([
      { text: 'Date', value: 'date' },
      { text: 'Ticket Count', value: 'tickets' },
      { text: 'Reserved', value: 'reserved' },
      { text: 'Remaining', value: 'remaining' },
      { text: 'Prices', value: 'prices' }
    ])
    const isLoaded = ref(false)

    async function patch (patch) {
      const res = await root.$feathers.service('theatre/show').patch(showId.value, patch)
      return res
    }

    function startEdit () {
      editDescription.value = description.value
      editActive.value = true
    }

    async function saveChanges () {
      await root.$feathers.service('theatre/show').patch(showId.value, {
        description: editDescription.value,
        showHeader: showHeader.value
      })
    }

    onMounted(async () => {
      let query = {
        active: true,
        shortName: shortName.value,
        $limit: 1
      }
      const { data } = await root.$feathers.service('theatre/show').find({ query })
      if (data.length === 0) {
        root.$dispatch('main/snackbar', { color: 'error', text: 'The show you attempted to view was not found' })
        root.$router.replace('/theatre')
        return
      }
      showId.value = data[0]._id
      showHeader.value = data[0].showHeader
      name.value = data[0].name
      description.value = data[0].description
      performances.value = data[0].performances.sort((a, b) => new Date(a.date) < new Date(b.date) ? -1 : 1).map((row) => {
        const dt = new Date(row.date)
        const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
        row.dateStr = `${date} at ${time}`
        return row
      })
      isLoaded.value = true
    })

    const imageDialog = ref(false)
    const imageUpload = ref(null)
    async function uploadImage () {
      if (imageUpload.value) {
        // Upload the file
        const formData = new FormData()
        formData.append('folder', 'theatre')
        formData.append('filename', showId.value)
        formData.append('file', imageUpload.value)

        const token = window.localStorage['feathers-jwt']
        const basePath = process.env.VUE_APP_API_URL + process.env.VUE_APP_API_PATH.replace('/socket.io', '')
        const res = await axios.post(basePath + '/system/image-upload', formData, { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` } })
        console.log(res)
      }
    }
    function handleImageError (error) {
      if (showId.value) {
        const regex = new RegExp(showId.value)
        if (regex.test(error)) {
          console.log('Error loading image', error)
          hideImage.value = true
        } else {
          hideImage.value = false
        }
      }
    }

    const addPerformanceDialog = ref(false)
    const performanceDate = ref('')
    const performanceTime = ref('19:00')
    const performanceLocation = ref('Sanderson Auditorium')
    const performanceTickets = ref(200)
    const prices = ref([])

    function updatePrice (index, upd) {
      prices.value.splice(index, 1, { ...prices.value[index], ...upd })
    }
    const isAdding = ref(false)

    async function addPerformance () {
      isAdding.value = true
      const obj = {
        date: new Date(performanceDate.value + ' ' + performanceTime.value),
        location: performanceLocation.value,
        tickets: performanceTickets.value,
        prices: prices.value
      }
      const res = await patch({ $push: { performances: obj } })
      performances.value = res.performances.sort((a, b) => new Date(a.date) < new Date(b.date) ? -1 : 1).map((row) => {
        const dt = new Date(row.date)
        const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
        row.dateStr = `${date} at ${time}`
        return row
      })
      isAdding.value = false
      addPerformanceDialog.value = false
    }

    const tickets = ref([])
    const first = ref('')
    const last = ref('')
    const email = ref('')
    function updateTickets (performance, { label, count }) {
      const index = tickets.value.findIndex(({ performance: perfId, label: l }) => perfId === performance._id && label === l)
      if (index >= 0) {
        tickets.value.splice(index, 1, { ...tickets.value[index], tickets: count })
      } else {
        const { price } = performance.prices.find(({ label: l }) => label === l)
        tickets.value.push({ performance: performance._id, date: performance.date, label, tickets: count, price, total: price * count })
      }
    }
    const paymentTotal = computed(() => tickets.value.reduce((prev, { total }) => prev + total, 0))

    const paymentPopup = ref(null)
    const paymentItems = computed(() => tickets.value.map(({ date, label, tickets, price }) => {
      return { name: `${name.value} on ${date}: ${label}`, quantity: tickets, unit_amount: { currency_code: 'USD', value: price } }
    }))
    function paymentSuccessful () {
      console.log('Payment was successful')
      // Open a dialog to tell them that the payment was successful
      // Trigger an email to be sent to them as their confirmation
      // Update the saved tickets record to show that it's been paid
    }

    const ticketId = ref(null)
    async function saveTickets () {
      // Save the tickets as a pending record, just in case the payment gets disconnected.
      if (ticketId.value) {
        await root.$feathers.service('/theatre/ticket').patch(ticketId.value, {
          first: isLoggedIn.value ? user.value.first : first.value,
          last: isLoggedIn.value ? user.value.last : last.value,
          email: isLoggedIn.value ? user.value.email : email.value,
          tickets: tickets.value,
          status: 'Reserved'
        })
      } else {
        const res = await root.$feathers.service('/theatre/ticket').create({
          first: isLoggedIn.value ? user.value.preferred : first.value,
          last: isLoggedIn.value ? user.value.last : last.value,
          email: isLoggedIn.value ? user.value.email : email.value,
          bannerId: isLoggedIn.value ? user.value.bannerId : null,
          show: showId.value,
          tickets: tickets.value
        })
        if (res) ticketId.value = res._id
      }
      const $inc = {}
      let index = 0
      const arrayFilters = []
      const idHash = {}
      tickets.value.forEach(({ performance, tickets }) => {
        if (performance in idHash) {
          $inc[`performances.$[perf${idHash[performance]}].reserved`] += tickets
        } else {
          $inc[`performances.$[perf${index}].reserved`] = tickets
          arrayFilters.push({ [`perf${index}._id`]: performance })
          idHash[performance] = index
          index++
        }
      })
      await root.$feathers.service('theatre/show').patch(showId.value, { $inc }, { query: { arrayFilters } })
      // We can use a script to load payment records from PayPal and find ones that aren't in the log
      // and match them up to these saved records and mark them as paid.
      paymentPopup.value.activate()
    }

    async function cancelTickets () {
      await root.$feathers.service('/theatre/ticket').patch(ticketId.value, { status: 'Cancelled' })
      const $inc = {}
      let index = 0
      const arrayFilters = []
      const idHash = {}
      tickets.value.forEach(({ performance, tickets }) => {
        if (idHash[performance]) {
          $inc[`performances.$[perf${idHash[performance]}].reserved`] = tickets * -1
        } else {
          $inc[`performances.$[perf${index}].reserved`] = tickets * -1
          arrayFilters.push({ [`perf${index}._id`]: performance })
          idHash[performance] = index
          index++
        }
      })
      await root.$feathers.service('theatre/show').patch(showId.value, { $inc }, { query: { arrayFilters } })
    }

    return {
      user,
      isLoggedIn,
      isAdmin,
      showId,
      shortName,
      showHeader,
      name,
      hideImage,
      description,
      editDescription,
      editActive,
      performances,
      performanceHeaders,
      startEdit,
      saveChanges,
      imageDialog,
      imageUpload,
      uploadImage,
      handleImageError,
      addPerformanceDialog,
      performanceDate,
      performanceTime,
      performanceLocation,
      performanceTickets,
      prices,
      updatePrice,
      isAdding,
      addPerformance,
      tickets,
      first,
      last,
      email,
      updateTickets,
      paymentTotal,
      paymentPopup,
      paymentItems,
      paymentSuccessful,
      saveTickets,
      cancelTickets
    }
  }
}
</script>
